export type NavItem = { label: string; href: string };

export const navItems: NavItem[] = [
  { label: "Features", href: "/features" },
  { label: "Install", href: "/install" },
  { label: "How it works", href: "/#how-it-works" },
  { label: "Screens", href: "/#screens" },
  { label: "Docs", href: "/docs" },
];

export const legalNavItems: NavItem[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Accessibility", href: "/accessibility" },
];

export type CapabilityItem = { label: string };

export const capabilityItems: CapabilityItem[] = [
  { label: "GST-ready POS billing" },
  { label: "Batch and expiry tracking" },
  { label: "Customer credit / due-khata" },
  { label: "Patient records and OPD visits" },
  { label: "AGPL-3.0; self-hosted by you" },
];

export type WorkflowStep = {
  step: number;
  title: string;
  body: string;
  image?: string;
  alt?: string;
};

export const workflowSteps: WorkflowStep[] = [
  {
    step: 1,
    title: "Receive stock",
    body: "Add a distributor purchase bill manually, or upload it to Inward OCR. Review extracted medicine, batch, expiry, quantity, and rate before it changes stock.",
    image: "/lifestyle-inventory-receiving.webp",
    alt: "A pharmacist checking a supplier invoice against the KinetiRx medicine stock management screen before updating inventory.",
  },
  {
    step: 2,
    title: "Bill at the counter",
    body: "Search medicines, dispense strips or loose units, and issue a GST invoice with live totals. Record cash, UPI, or card payment.",
    image: "/lifestyle-pos-counter.webp",
    alt: "A pharmacist using the KinetiRx POS billing screen at the counter, with a printed GST receipt coming off the printer.",
  },
  {
    step: 3,
    title: "Keep records current",
    body: "Track expiry, rack location, distributors, patient history, and due-khata from the same system of record.",
    image: "/screenshot-inventory.webp",
    alt: "KinetiRx inventory screen with medicine batch, expiry date, rack location, and distributor details.",
  },
  {
    step: 4,
    title: "Close with confidence",
    body: "Reconcile opening cash, denominations, payment split, expenses, and closing difference—then see the day’s numbers on the dashboard.",
    image: "/screenshot-dashboard.webp",
    alt: "KinetiRx pharmacy dashboard showing today’s sales, drawer cash, stock valuation, and seven-day financial flow.",
  },
];

export type ScreenStory = {
  title: string;
  body: string;
  src: string;
  alt: string;
};

export const screenStories: ScreenStory[] = [
  {
    title: "Start every day with the pharmacy dashboard",
    body: "Use it to review today’s sales, drawer cash, stock valuation, and the last seven days of financial flow before the counter gets busy.",
    src: "/screenshot-dashboard.webp",
    alt: "KinetiRx pharmacy dashboard showing today’s sales, drawer cash, stock valuation, and seven-day financial flow.",
  },
  {
    title: "Create a GST bill while serving a customer",
    body: "Search a medicine, adjust strip or loose quantity, confirm the payment method, and print or save the GST invoice from one checkout screen.",
    src: "/screenshot-pos.webp",
    alt: "KinetiRx POS billing screen with medicines, quantities, GST totals, and payment options.",
  },
  {
    title: "Catch inventory issues before the shelf is empty",
    body: "Use batch, expiry, rack location, and distributor information to know what is in stock and what needs attention.",
    src: "/screenshot-inventory.webp",
    alt: "KinetiRx inventory screen with medicine batch, expiry date, rack location, and distributor details.",
  },
  {
    title: "Find a patient’s history without digging through paper",
    body: "Open a patient profile to view visit history, purchases, outstanding dues, and doctor assignment in context.",
    src: "/screenshot-patients.webp",
    alt: "KinetiRx patient profile showing visit history, purchases, dues, and doctor assignment.",
  },
  {
    title: "Make due-khata searchable and auditable",
    body: "Record a credit sale, view the current balance, and log partial payments with a timestamped history.",
    src: "/screenshot-due-khata.webp",
    alt: "KinetiRx due-khata screen showing customer balances, credit sales, and payment history.",
  },
];

export type OutcomeCard = { title: string; body: string; icon: string; href: string };

export const outcomeCards: OutcomeCard[] = [
  {
    title: "Bill accurately at the counter",
    body: "POS checkout, GST invoices, loose/strip dispensing, invoice settings, and printer configuration.",
    icon: "receipt-indian-rupee",
    href: "/docs#pos",
  },
  {
    title: "Protect stock value",
    body: "Batch and expiry tracking, distributor records, inventory, and medicine ordering.",
    icon: "package",
    href: "/docs#inventory",
  },
  {
    title: "Manage customer credit",
    body: "Due-khata balances, partial payments, searchable history, and reminders where configured.",
    icon: "wallet",
    href: "/docs#patients",
  },
  {
    title: "Keep patient context together",
    body: "Profiles, purchase history, OPD visits, blood-test tracking, and follow-up reminders.",
    icon: "heart-pulse",
    href: "/docs#patients",
  },
  {
    title: "Finish the day cleanly",
    body: "Opening cash, denominations, payment split, expenses, and reconciliation.",
    icon: "calculator",
    href: "/docs#pos",
  },
  {
    title: "Give staff only the access they need",
    body: "Role-based accounts and server-enforced permissions.",
    icon: "users",
    href: "/docs#security",
  },
];

export type SelfHostingCard = { title: string; body: string; icon: string };

export const selfHostingCards: SelfHostingCard[] = [
  {
    title: "No recurring per-shop SaaS fee",
    body: "KinetiRx is AGPL-3.0 licensed with no recurring per-shop subscription; you cover your own hardware or server cost.",
    icon: "shield-off",
  },
  {
    title: "Your database",
    body: "Sales, inventory, patients, and dues run in PostgreSQL that you operate.",
    icon: "database-zap",
  },
  {
    title: "Works locally",
    body: "Core pharmacy workflows run without a third-party cloud dependency. OCR is optional and only contacts Gemini when you provide and configure that key.",
    icon: "wifi-off",
  },
  {
    title: "Your upgrade schedule",
    body: "Deploy and update on your own schedule.",
    icon: "server-cog",
  },
];

export type SetupStep = { title: string; body: string };

export const setupSteps: SetupStep[] = [
  {
    title: "Get the code",
    body: "Clone KinetiRx from GitHub onto the machine that will run it — a spare PC, a home server, or a VPS.",
  },
  {
    title: "Set initial admin & database secrets",
    body: "Copy the environment example and set a Postgres password, a JWT signing secret, and your first admin password. A Gemini API key is optional, for AI invoice OCR.",
  },
  {
    title: "Start the stack",
    body: "Run the documented Docker Compose command. PostgreSQL, the Go backend, and the frontend start together, and first-run migrations create the application database.",
  },
];

export type DeployStep = { title: string; body: string; code?: string };

export const deploySteps: DeployStep[] = [
  {
    title: "Clone the repository",
    body: "Get the source onto the machine that will run it — a spare PC, a home server, or a VPS.",
    code: "git clone https://github.com/Raktim94/KinetiRx.git\ncd KinetiRx",
  },
  {
    title: "Configure secrets",
    body: "Copy the example env file and set a Postgres password, a JWT signing secret, and the first admin password. A Gemini API key is optional — add it later to enable AI invoice OCR.",
    code: "cp deploy/.env.example deploy/.env\n# edit deploy/.env: POSTGRES_PASSWORD, JWT_SECRET, KINETIRX_ADMIN_PASSWORD",
  },
  {
    title: "Start the stack",
    body: "One command brings up PostgreSQL, the Go backend, and the nginx-served frontend together. Migrations run automatically on first boot.",
    code: "docker compose -f deploy/docker-compose.yml --env-file deploy/.env up -d --build",
  },
];

export type FAQ = { q: string; a: string };

export const faqs: FAQ[] = [
  {
    q: "Is KinetiRx really free pharmacy management software?",
    a: "Yes. KinetiRx is self-hosted and licensed under AGPL-3.0, with no monthly per-shop subscription or per-user fee. You provide the computer, NAS, or VPS and cover its infrastructure costs. You may use, modify, and redistribute the source under the AGPL-3.0 terms; if you run a modified version as a network service, the license requires offering its source to users.",
  },
  {
    q: "Who is KinetiRx for?",
    a: "Independent pharmacies and small clinics that want a real system of record — billing, stock, patients, dues — instead of a spreadsheet, and are comfortable self-hosting or have someone who can help deploy it, rather than paying a SaaS vendor a monthly per-shop fee.",
  },
  {
    q: "Can I use it without AI invoice scanning?",
    a: "Yes. Manual inward entry is available for every purchase bill. Inward OCR is optional and requires a Gemini API key you configure yourself — without one, the rest of KinetiRx, including billing, inventory, due-khata, and patient records, works exactly the same.",
  },
  {
    q: "What is due-khata, exactly?",
    a: "Udhaar or “khata” is the informal running-credit tab many Indian shops and pharmacies keep for trusted regular customers — they take medicine now and settle up later, sometimes in parts. Due-Khata is that same ledger digitized: every due amount, every partial payment, and the full history against a customer, instead of a paper notebook only the counter staff can read.",
  },
  {
    q: "Does the AI invoice scanning require an internet connection?",
    a: "The core system — POS, inventory, patients, due-khata, everything — runs entirely on your own Postgres database with no cloud dependency. Inward OCR (the AI purchase-bill scanner) is the one optional exception: it uses Google Gemini and needs an API key you supply. Without a key configured, that one feature falls back to a manual entry form — the rest of the app is unaffected.",
  },
  {
    q: "What hardware do I need to self-host it?",
    a: "Anything that runs Docker: a spare PC, a home NAS, or a small VPS. The stack is three containers — PostgreSQL, the Go backend, and an nginx-served React frontend — started with a single docker compose command.",
  },
  {
    q: "How do I install it?",
    a: "Clone the repository, copy deploy/.env.example to deploy/.env, fill in a Postgres password, a JWT secret, and an admin password, then run docker compose up. The app opens on the port you configured, and the first login uses the admin credentials you just set.",
  },
  {
    q: "Can I install it on CasaOS?",
    a: "A CasaOS App Store manifest (x-casaos v2) is included in the repository and ready to submit. It isn't published to the CasaOS App Store yet — once it is, installing will be a single click from the CasaOS UI. Until then, Docker Compose works on any CasaOS box today.",
  },
  {
    q: "Does it handle GST billing correctly?",
    a: "Yes — POS checkout produces a proper GST invoice, and Invoice Settings lets you store your GST and drug-license numbers, invoice numbering scheme, and letterhead so every bill is correctly formatted from the first sale.",
  },
  {
    q: "Can I use it with Claude or another AI assistant?",
    a: "Yes. An included MCP server (run via a Docker Compose profile) lets Claude Desktop or Claude Code look up inventory, check a patient's due-khata balance, pull the daily register, or record a sale — using the permissions of whichever employee credentials it's configured with.",
  },
];

export type ComparisonRow = {
  feature: string;
  kinetirx: boolean | string;
  saas: boolean | string;
  spreadsheet: boolean | string;
};

export const comparisonRows: ComparisonRow[] = [
  { feature: "Ongoing cost",          kinetirx: "Your server only", saas: "Monthly subscription", spreadsheet: "Free" },
  { feature: "Data stays on your server", kinetirx: true,           saas: false,                  spreadsheet: true },
  { feature: "GST-compliant POS billing", kinetirx: true,           saas: true,                   spreadsheet: false },
  { feature: "Batch/expiry inventory",    kinetirx: true,           saas: "Paid add-on",          spreadsheet: "Manual" },
  { feature: "Due-khata credit ledger",   kinetirx: true,           saas: "Rare",                 spreadsheet: "Manual" },
  { feature: "Patient records & OPD",     kinetirx: true,           saas: "Paid tier",             spreadsheet: false },
  { feature: "AI invoice OCR",            kinetirx: "Optional (Gemini)", saas: "Rare",             spreadsheet: false },
  { feature: "Role-based staff accounts", kinetirx: true,           saas: true,                   spreadsheet: false },
  { feature: "No vendor lock-in",         kinetirx: true,           saas: false,                  spreadsheet: true },
];
